import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { trackAdminBooking } from "../../api/adminWalkInBookingApi";
import { formatCurrency } from "../../utils/formatCurrency";
import VerticalProgressBar from "../Dashboard/UserDashboard/SharedComponents/VerticalProgressBar";

const DetailRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[6.5rem_minmax(0,1fr)] gap-0.5 sm:gap-2 py-1.5 border-b border-stone-100 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
        {label}
      </span>
      <span className="text-sm text-stone-900 break-words min-w-0">
        {value}
      </span>
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="rounded-lg border border-stone-200 bg-white px-3 py-2 min-w-0 overflow-hidden">
    <p className="text-[10px] font-bold uppercase text-stone-500">{label}</p>
    <p className="text-sm font-semibold text-stone-900 break-words mt-0.5">
      {value || "—"}
    </p>
  </div>
);

const PartyCard = ({ title, name, email, phone, address }) => (
  <div className="rounded-xl border border-stone-200 bg-white p-3 min-w-0">
    <p className="text-[11px] font-bold uppercase tracking-wide text-orange-600 mb-2">
      {title}
    </p>
    <p className="font-semibold text-stone-900">{name || "—"}</p>
    <DetailRow label="Email" value={email} />
    <DetailRow label="Phone" value={phone} />
    <DetailRow label="Address" value={address} />
  </div>
);

const PublicTrackPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [track, setTrack] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await trackAdminBooking(token);
        if (!cancelled) setTrack(data);
      } catch (err) {
        if (!cancelled) {
          setTrack(null);
          setError(
            err?.response?.status === 404
              ? "This tracking link is invalid or has expired."
              : err?.response?.data?.message ||
                  err?.message ||
                  "Unable to load tracking details.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) run();
    else {
      setLoading(false);
      setError("Missing tracking token.");
    }
    return () => {
      cancelled = true;
    };
  }, [token]);

  const serial = track?.parcelSerial || track?.recordSerialNo;
  const products = Array.isArray(track?.products) ? track.products : [];
  const statusTimeline = Array.isArray(track?.statusTimeline)
    ? track.statusTimeline
    : [];

  return (
    <div className="min-h-[70vh] bg-[#f5f3f0] px-4 py-10">
      <div className="max-w-4xl mx-auto rounded-2xl border border-stone-200 bg-white shadow-sm p-5 sm:p-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-orange-600">
          Parcel tracking
        </p>
        <h1 className="text-2xl font-semibold text-stone-900 mt-1">
          Track your shipment
        </h1>

        {loading ? (
          <p className="mt-6 text-sm text-stone-500">Loading tracking details…</p>
        ) : error ? (
          <p className="mt-6 text-sm text-rose-600">{error}</p>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <SummaryCard label="Amount" value={formatCurrency(track?.amount, track?.currency || "GBP")} />
              <SummaryCard label="Payment" value={track?.paymentStatusName} />
              <SummaryCard label="Parcel status" value={track?.status} />
              <SummaryCard label="Latest invoice" value={track?.paymentInvoiceNo} />
            </div>

            {serial || track?.routeName ? (
              <VerticalProgressBar
                embedded
                statusData={statusTimeline}
                loading={false}
                currentStatus={track?.status}
                routeName={track?.routeName}
                parcelNo={serial || track?.parcelId}
              />
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <PartyCard
                title="Sender"
                name={track?.senderName}
                email={track?.senderEmail}
                phone={track?.senderPhone}
                address={track?.senderAddress}
              />
              <PartyCard
                title="Receiver"
                name={track?.receiverName}
                email={track?.receiverEmail}
                phone={track?.receiverPhone}
                address={track?.receiverAddress}
              />
            </div>

            <div className="rounded-xl border border-stone-200 bg-white p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-orange-600 mb-2">
                Shipment
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                <DetailRow label="Route" value={track?.routeName} />
                <DetailRow label="Cargo" value={track?.cargoTypeName} />
                <DetailRow label="Record serial" value={serial} />
                <DetailRow label="Item" value={track?.uniqItemDescription} />
                <DetailRow label="Service" value={track?.shippingServiceName} />
                <DetailRow label="Method" value={track?.paymentMethodName} />
              </div>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white p-3 overflow-hidden">
              <p className="text-[11px] font-bold uppercase tracking-wide text-orange-600 mb-2">
                Product details
              </p>
              {products.length === 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  <DetailRow label="Item" value={track?.uniqItemDescription} />
                  <DetailRow label="Weight" value={track?.weightDescription} />
                  <DetailRow label="Dimension" value={track?.dimensionName} />
                  <DetailRow label="Cargo" value={track?.cargoTypeName} />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wide text-stone-500">
                      <tr>
                        <th className="text-left py-2 pr-3 font-bold">Description</th>
                        <th className="text-left py-2 pr-3 font-bold">Type</th>
                        <th className="text-right py-2 pr-3 font-bold">Qty</th>
                        <th className="text-left py-2 pr-3 font-bold">Dimensions</th>
                        <th className="text-left py-2 font-bold">Weight</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {products.map((item, idx) => {
                        const dims = [item.length, item.width, item.height]
                          .filter((v) => v !== "" && v != null)
                          .join("×");
                        return (
                          <tr key={`${item.itemDescription}-${idx}`}>
                            <td className="py-2 pr-3 text-stone-900">
                              {item.itemDescription || "—"}
                              {item.categoryName ? (
                                <span className="block text-xs text-stone-500">
                                  {item.categoryName}
                                </span>
                              ) : null}
                            </td>
                            <td className="py-2 pr-3 text-stone-700">
                              {item.typeName || item.dimensionName || "—"}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">
                              {item.qty}
                            </td>
                            <td className="py-2 pr-3 text-stone-700">
                              {dims ? `${dims} in` : item.dimensionName || "—"}
                            </td>
                            <td className="py-2 text-stone-700">
                              {item.weight ? `${item.weight} kg` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        <Link
          to="/"
          className="mt-6 inline-flex text-sm font-semibold text-orange-700 hover:underline"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
};

export default PublicTrackPage;
